<script setup lang="ts">
import { DescriptionProps } from "./Description.props"
import {
  BadgeType,
  MinisterialBadge
} from "~/components/cms/ComponentMinisterialBadges.props"
import { getFormatLongDate } from "~/utils/date"

const props = defineProps<DescriptionProps>()

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim()

const visibleDescriptions = computed(() =>
  props.descriptions.filter(
    (d) => d.description && stripHtml(d.description).length > 0
  )
)

const isInfoExpanded = ref(false)

// Centralizes icon, special validation logic, and custom string rendering for product information fields.
const PRODUCT_INFO_SCHEMA = [
  { key: "activePrinciple", icon: "OutlinePharmacy.svg" },
  { key: "howToUse", icon: "BookOpen.svg" },
  { key: "dosage", icon: "ColorPicker.svg" },
  { key: "dosageType", icon: "Archive.svg" },
  {
    key: "leafletSlug",
    icon: "Post.svg",
    titleKey: "leaflet",
    render: (infos: any) =>
      `[${$t("productPage.description.checkLeaflet")}](${infos.leafletSlug})`
  },
  { key: "category", icon: "Folder.svg" },
  { key: "unitOfMeasure", icon: "Droplet.svg" },
  {
    key: "correspondingGenericDrug",
    icon: "OutlineMedicine.svg",
    validate: (infos: any) => !!infos.correspondingGenericDrug?.productName,
    render: (infos: any) =>
      `[${infos.correspondingGenericDrug?.productName}](${infos.correspondingGenericDrug?.slug})`
  },
  {
    key: "dueDate",
    icon: "Info.svg",
    render: (infos: any) => getFormatLongDate(infos?.dueDate)
  },
  { key: "productCode", icon: "Box.svg" },
  {
    key: "firm",
    icon: "Home.svg",
    validate: (infos: any) => !!infos.firm?.name,
    render: (infos: any) => `[${infos.firm?.name}](${infos.firm?.slug})`
  },
  { key: "producer", icon: "Pricetag.svg" }
] as const

type InfoKey = typeof PRODUCT_INFO_SCHEMA[number]["key"]

interface InfoField {
  key: InfoKey
  icon: string
  titleKey?: string
  validate?: (infos: any) => boolean
  render?: (infos: any) => string
}

// Filters and maps product info fields according to the schema, caching the result for performance optimization.
const activeInfoRows = computed(() => {
  const infos = props.infos
  if (!infos) return []

  return (PRODUCT_INFO_SCHEMA as readonly InfoField[])
    .filter((field: InfoField) =>
      field.validate
        ? field.validate(infos)
        : Boolean(infos[field.key as keyof typeof infos])
    )
    .map((field: InfoField) => ({
      key: field.key,
      icon: field.icon,
      titleKey: field.titleKey ?? field.key,
      description: field.render
        ? field.render(infos)
        : String(infos[field.key as keyof typeof infos] || "")
    }))
})

const lastVisibleInfoKey = computed(() => {
  if (isInfoExpanded.value || activeInfoRows.value.length <= 6) return null
  return activeInfoRows.value[5]?.key || null
})

const getFadeClass = (key: InfoKey) => {
  return lastVisibleInfoKey.value === key ? "product-info-fade" : ""
}

const DEFAULT_ACCORDION_LABEL = $t("productPage.description.descriptionTitle")

const isShowMore = reactive<Record<string, boolean>>({})
const isVisibleShowMore = ref<Record<string, boolean>>({})

if (import.meta.server) {
  visibleDescriptions.value.forEach((desc) => {
    isShowMore[desc.title] = false
  })
}

onMounted(() => {
  visibleDescriptions.value.forEach((desc) => {
    isShowMore[desc.title] = isVisibleShowMore.value[desc.title] =
      props.isSopOtc ? false : true
  })
})

const toggleFullDescription = (key: string) => {
  isShowMore[key] = !isShowMore[key]
}

const handleSetDescriptionOverflow = (key: string, overflow: boolean) => {
  isVisibleShowMore.value[key] = overflow
}

const dayjs = useDayjs()

const { MinisterialBadgesList } = await useFooterConnect()

const handleDownloadLeaflet = () => {
  if (props.infos) navigateTo(props.infos.leafletSlug, { external: true })
}

const getAccordionLabel = (desc: any) =>
  desc.labelAccordion || DEFAULT_ACCORDION_LABEL //Hardcoded value, to be improved

const groupedDescriptions = computed(() => {
  const groups: { label: string; items: typeof visibleDescriptions.value }[] =
    []
  const labelMap = new Map<string, typeof visibleDescriptions.value>()

  for (const desc of visibleDescriptions.value) {
    const label = getAccordionLabel(desc)
    if (!labelMap.has(label)) {
      const items: typeof visibleDescriptions.value = []
      labelMap.set(label, items)
      groups.push({ label, items })
    }
    labelMap.get(label)!.push(desc)
  }

  return groups
})

const ministerialBadges = computed<MinisterialBadge[]>(() =>
  MinisterialBadgesList.value.filter((item: MinisterialBadge) => {
    if (!props.infos.productCode) {
      return false
    }

    switch (props.infos.productCode[0]) {
      case "0":
        return item.badgeType === BadgeType.sopOtc
      case "1":
        return item.badgeType === BadgeType.vet
      default:
        return false
    }
  })
)

const handleLink = () => {
  const idKey = "supporto" // Yes, this is hardcoded // TODO: improve it

  const target = document.getElementById(idKey)
  if (target) {
    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start"
    })
    history.replaceState(null, "", `#${idKey}`)
  }
}

const { legalDisclaimer } = useLegalDisclaimer()
</script>

<template>
  <div class="container-product_page-description">
    <Disclosure
      v-slot="{ open, close }"
      defaultOpen
      v-if="props.infos.leafletSlug"
    >
      <!-- <div class="hidden">{{ (closeFn = close) }}</div> -->
      <DisclosureButton class="flex w-full justify-between py-3">
        <h2 class="dolphin-bold md:elephant-bold mb-4">
          {{ $t("productPage.description.infosTitle") }}
        </h2>

        <UtilsIcon
          name="ChevronDown.svg"
          class="h-6 w-6"
          :class="open && 'rotate-180 transform'"
        />
      </DisclosureButton>
      <DisclosurePanel class="text-gray-500">
        <!-- Property Table -->
        <div v-if="infos" class="divide-grey-100 divide-y">
          <template v-for="(row, index) in activeInfoRows" :key="row.key">
            <UtilsPropertyRow
              v-if="isInfoExpanded || index < 6"
              :title="
                $t(`productPage.description.infos.${row.titleKey ?? row.key}`)
              "
              :description="row.description"
              :class="getFadeClass(row.key)"
            >
              <template #icon>
                <UtilsIcon :name="row.icon" color="black-80" />
              </template>
            </UtilsPropertyRow>
          </template>

          <UtilsButton
            v-if="activeInfoRows.length > 6"
            @click="isInfoExpanded = !isInfoExpanded"
            theme="link-green"
            class="
              custom-btn-no-padding custom-btn-start
              beaver-medium
              md:pig-medium
              mb-8
              mt-4
              border-none
            "
            :text="
              isInfoExpanded ? $t('general.show.less') : $t('general.show.more')
            "
          />
        </div>
      </DisclosurePanel>
    </Disclosure>
    <!-- Product Description -->
    <div v-if="groupedDescriptions.length">
      <Disclosure
        v-for="(group, key) in groupedDescriptions"
        :key="group.label"
        :defaultOpen="group.label === DEFAULT_ACCORDION_LABEL || props.isSopOtc"
        v-slot="{ open }"
      >
        <DisclosureButton
          :class="{
            'border-black-20 flex w-full justify-between py-6 text-left': true,
            'border-t':
              props?.infos?.leafletSlug || key === undefined || key > 0,
            'border-t-0': !props.infos?.leafletSlug && index === 0
          }"
        >
          <h3 class="dolphin-bold md:elephant-bold">
            {{ group.label }}
          </h3>

          <UtilsIcon
            name="ChevronDown.svg"
            class="h-6 w-6"
            :class="open && 'rotate-180 transform'"
          />
        </DisclosureButton>

        <DisclosurePanel
          class="
            text-black-80
            bg-grey-main
            mb-3
            space-y-3
            rounded-md
            p-5
            md:rounded-lg
            md:p-6
          "
        >
          <template v-for="desc in group.items" :key="desc.title">
            <h4 v-if="group.items.length > 1" class="beaver-bold md:pig-bold">
              {{ desc.title }}
            </h4>
            <UtilsMarkdown
              v-if="desc.description"
              :content="desc.description"
              tag="div"
              class="
                mouse
                md:beaver
                text-black-80
                [&>p:last-child]:mb-0
                [&>p]:mb-4
              "
              :class="{
                'product-description-closed': isShowMore[desc.title] !== false,
                'product-description-fade':
                  isVisibleShowMore[desc.title] &&
                  isShowMore[desc.title] !== false
              }"
              @on-change-overflow="(overflow: boolean) => handleSetDescriptionOverflow(desc.title, overflow)"
            />

            <UtilsButton
              v-if="
                isVisibleShowMore[desc.title] ||
                (isShowMore[desc.title] === false && !props.isSopOtc)
              "
              @click="toggleFullDescription(desc.title)"
              theme="link-green"
              class="
                custom-btn-no-padding custom-btn-start
                beaver-medium
                md:pig-medium
              "
              :text="
                isShowMore[desc.title] !== false
                  ? $t('general.show.more')
                  : $t('general.show.less')
              "
            />
          </template>

          <UtilsButton
            v-if="props.infos.leafletSlug"
            @click="handleDownloadLeaflet"
            class="
              custom-btn-underlined custom-btn-only-beaver custom-btn-no-padding
              mb-6
              mt-3
              block
            "
            theme="ghost-green"
            :text="$t('productPage.description.checkLeaflet')"
            order="last"
          >
            <template #icon>
              <UtilsIcon name="Download.svg" color="green-main" />
            </template>
          </UtilsButton>
          <ProductPageUtilsMinisterialBanner
            class="mb-10"
            v-if="ministerialBanner"
            v-bind="ministerialBanner"
          />
        </DisclosurePanel>
      </Disclosure>
    </div>

    <!-- Important Infos -->
    <Disclosure
      v-slot="{ open }"
      :default-open="props.isSopOtc"
      v-if="importantInfos.length || props.infos.leafletSlug"
    >
      <DisclosureButton
        class="border-black-20 flex w-full justify-between border-t py-6"
      >
        <span class="dolphin-bold md:elephant-bold">
          {{ $t("productPage.description.importantInfosTitle") }}
        </span>

        <UtilsIcon
          name="ChevronDown.svg"
          class="h-6 w-6"
          :class="open && 'rotate-180 transform'"
        />
      </DisclosureButton>
      <DisclosurePanel class="text-gray-500">
        <div v-if="importantInfos.length" class="space-y-6">
          <UtilsProperty v-for="info in importantInfos" v-bind="info" />
        </div>

        <UtilsButton
          v-if="props.infos.leafletSlug"
          @click="handleDownloadLeaflet"
          class="
            custom-btn-underlined custom-btn-only-beaver custom-btn-no-padding
            mb-6
            mt-3
            block
          "
          theme="ghost-green"
          :text="$t('productPage.description.checkLeaflet')"
          order="last"
        >
          <template #icon>
            <UtilsIcon name="Download.svg" color="green-main" />
          </template>
        </UtilsButton>
      </DisclosurePanel>
    </Disclosure>

    <div class="border-black-20 mb-6 border-b md:mb-0"></div>

    <div
      v-if="legalDisclaimer && !props.isSopOtc"
      class="text-black-80 my-6 italic"
    >
      <p class="mouse">{{ legalDisclaimer }}</p>
    </div>

    <ProductPageBannerFaqInline
      v-if="hasFaqs"
      :title="$t('productPage.description.bannerTitle')"
      :text="$t('productPage.description.bannerLinkText')"
      @linkClick="handleLink"
    />

    <div class="mb-5 mt-0 w-fit md:mb-0 md:mt-5">
      <CmsComponentMinisterialBadges
        v-if="ministerialBadges"
        :Badges="ministerialBadges"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
:focus-visible {
  outline: none;
}
</style>
